// usePersistentToggle (TASK-650) — a boolean UI preference persisted to
// localStorage as 'shown'/'hidden'. Used for the dashboard rails and the input
// dock chip rows so the layout survives reloads. SSR-guarded.
import { useCallback, useState } from 'react';

export function readPersistentToggle(key: string, defaultValue: boolean): boolean {
  if (typeof window === 'undefined') return defaultValue;
  const stored = window.localStorage.getItem(key);
  if (stored === 'shown') return true;
  if (stored === 'hidden') return false;
  return defaultValue;
}

export function usePersistentToggle(
  key: string,
  defaultValue = true,
): [boolean, () => void, (next: boolean) => void] {
  const [visible, setVisible] = useState(() => readPersistentToggle(key, defaultValue));

  const setPersisted = useCallback(
    (next: boolean) => {
      window.localStorage.setItem(key, next ? 'shown' : 'hidden');
      setVisible(next);
    },
    [key],
  );
  const toggle = useCallback(() => {
    setVisible((prev) => {
      const next = !prev;
      window.localStorage.setItem(key, next ? 'shown' : 'hidden');
      return next;
    });
  }, [key]);

  return [visible, toggle, setPersisted];
}

/**
 * usePersistentChoice (TASK-924) — the same preference pattern for a value that is one of a
 * KNOWN set rather than a boolean. Used for the Today desk lens (`kielis-rail-lens`).
 *
 * State is what the user CHOSE, not what is currently shown: nothing stored means the caller's
 * `defaultValue` shows through, and that default is allowed to arrive late. It has to be —
 * the Today lens defaults by role, and `AccountContext` sets `user` in an effect, so a lazy
 * initializer would bake in the anonymous default and hand a compliance officer the
 * everything-view on every first paint. Once a chip is clicked the stored value wins for good.
 *
 * The stored string is validated against `options` on read: a lens written by an older build,
 * or hand-edited, falls back to the default rather than filtering the surface down to a
 * category that no longer exists — an attention surface that silently shows nothing is worse
 * than one that shows everything.
 */
export function usePersistentChoice<T extends string>(
  key: string,
  options: readonly T[],
  defaultValue: T,
): [T, (next: T) => void] {
  const [chosen, setChosen] = useState<T | null>(() => {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem(key) as T | null;
    return stored && options.includes(stored) ? stored : null;
  });

  const setPersisted = useCallback(
    (next: T) => {
      window.localStorage.setItem(key, next);
      setChosen(next);
    },
    [key],
  );

  return [chosen ?? defaultValue, setPersisted];
}
